import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReportTemplate from '../../components/reports/ReportTemplate';
import { ROUTES } from '../../consts/general';
import { PICK_PACK_REPORT } from '../../consts/reports';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import { formatNumber } from '../warehouse/format';

const TIME_ZONE = 'America/Los_Angeles';
const MIN_RATE_SPAN_SECONDS = 15 * 60;
const DERIVED = '†';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const pacificToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
const shiftDay = (day, count) => {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + count);
  return date.toISOString().slice(0, 10);
};
const mmddyyyy = (day) => (day ? `${day.slice(5, 7)}/${day.slice(8, 10)}/${day.slice(0, 4)}` : '—');
const weekday = (day) => new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: 'UTC' }).format(new Date(`${day}T12:00:00Z`));
const time = (value) => (value ? new Intl.DateTimeFormat('en-US', { timeZone: TIME_ZONE, hour: 'numeric', minute: '2-digit' }).format(new Date(value)) : '—');
const span = (seconds) => {
  if (seconds == null) return '—';
  const minutes = Math.round(seconds / 60);
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;
};
const gap = (seconds) => {
  if (seconds == null) return '—';
  const total = Math.round(seconds);
  if (total >= 3600) return `${Math.floor(total / 3600)}h ${String(Math.floor((total % 3600) / 60)).padStart(2, '0')}m`;
  if (total >= 60) return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, '0')}s`;
  return `${total}s`;
};
const rate = (count, seconds) => (seconds != null && seconds >= MIN_RATE_SPAN_SECONDS ? formatNumber(Math.round(count / (seconds / 3600))) : '—');
const pct = (part, whole) => (whole ? `${Math.round((100 * part) / whole)}%` : '—');

const STATUS_STYLE = {
  final: 'bg-emerald-100 text-emerald-800',
  partial: 'bg-amber-100 text-amber-800',
  not_imported: 'bg-gray-100 text-gray-600',
};
const statusLabel = (day) => {
  if (day.status === 'final') return 'Final';
  if (day.status === 'partial') return `Through ${time(day.windowEnd)} PT`;
  return 'Not imported';
};

const PICK_COLUMNS = ['Date', 'Picker', 'Orders', 'Pick lines', 'Units', 'Totes', 'Bin barcode scanned', 'First pick', 'Last pick', `Span${DERIVED}`, `Units / span hr${DERIVED}`, `Orders / span hr${DERIVED}`, `Median gap${DERIVED}`, `Longest gap${DERIVED}`];
const PACK_COLUMNS = ['Date', 'Packer', 'Pack records', 'Shipments', 'Orders', 'Units', 'Unique items', 'Item barcodes scanned', 'First pack', 'Last pack', `Span${DERIVED}`, `Units / span hr${DERIVED}`, `Shipments / span hr${DERIVED}`, `Median gap${DERIVED}`, `Longest gap${DERIVED}`];

const pickCells = (row) => [mmddyyyy(row.work_date), row.worker_name, formatNumber(row.orders), formatNumber(row.records), formatNumber(row.units), formatNumber(row.totes), pct(row.scanned, row.records), time(row.first_scan_at), time(row.last_scan_at), span(row.span_seconds), rate(row.units, row.span_seconds), rate(row.orders, row.span_seconds), gap(row.median_gap_seconds), gap(row.longest_gap_seconds)];
const packCells = (row) => [mmddyyyy(row.work_date), row.worker_name, formatNumber(row.records), formatNumber(row.shipments), formatNumber(row.orders), formatNumber(row.units), formatNumber(row.unique_items), pct(row.scanned, row.units), time(row.first_scan_at), time(row.last_scan_at), span(row.span_seconds), rate(row.units, row.span_seconds), rate(row.shipments, row.span_seconds), gap(row.median_gap_seconds), gap(row.longest_gap_seconds)];

const DataTable = ({ columns, rows, empty }) => (
  <div className="overflow-auto rounded border bg-white">
    {rows.length === 0 ? (
      <div className="px-6 py-8 text-center text-sm text-gray-500">{empty}</div>
    ) : (
      <table className="min-w-max text-left text-xs">
        <thead className="sticky top-0 bg-gray-100">
          <tr>{columns.map((label) => <th key={label} className="whitespace-nowrap px-3 py-2">{label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((cells, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={index} className={index % 2 ? 'bg-gray-50' : ''}>
              {cells.map((value, cellIndex) => (
                // eslint-disable-next-line react/no-array-index-key
                <td key={cellIndex} className={`whitespace-nowrap px-3 py-2 ${cellIndex === 1 ? 'font-semibold' : ''}`}>{value}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const Bar = ({ value, max, color }) => (
  <div className="h-2 w-24 rounded bg-gray-100">
    <div className={`h-2 rounded ${color}`} style={{ width: `${max ? Math.round((100 * value) / max) : 0}%` }} />
  </div>
);

const personTotals = (rows, operation) => {
  const groups = new Map();
  rows.filter((row) => row.operation === operation).forEach((row) => {
    const entry = groups.get(row.shiphero_user_id) || { name: row.worker_name, days: 0, units: 0, orders: 0, shipments: 0, records: 0 };
    entry.days += 1;
    entry.units += row.units;
    entry.orders += row.orders;
    entry.shipments += row.shipments || 0;
    entry.records += row.records;
    groups.set(row.shiphero_user_id, entry);
  });
  return [...groups.values()].sort((left, right) => right.units - left.units);
};

const PickPackReport = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);
  const today = pacificToday();
  const to = searchParams.get('to') || today;
  const from = searchParams.get('from') || shiftDay(to, -6);
  const [data, setData] = useState({ trend: [], rows: [], coverage: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [actionError, setActionError] = useState('');

  const load = useCallback(async () => {
    const { signal, requestId } = beginRequest();
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/v1/reports/pick-pack?${new URLSearchParams({ from, to })}`, { headers: authHeaders(), signal });
      const json = await response.json();
      if (!response.ok) throw new Error(json.error || 'Failed to load Pick & Pack report');
      if (!isLatest(requestId)) return;
      setData(json);
    } catch (requestError) {
      if (requestError?.name === 'AbortError' || !isLatest(requestId)) return;
      setError(requestError.message);
    } finally {
      if (isLatest(requestId)) setLoading(false);
    }
  }, [beginRequest, isLatest, from, to]);

  useEffect(() => {
    if (initialReady) load();
  }, [initialReady, load]);

  const updateParams = (next) => {
    const params = { ...Object.fromEntries(searchParams.entries()), ...next };
    Object.keys(params).forEach((key) => { if (!params[key]) delete params[key]; });
    setSearchParams(params);
  };

  const downloadPdf = async () => {
    setDownloading(true);
    setActionError('');
    try {
      const response = await fetch(`/api/v1/reports/pick-pack/export?${new URLSearchParams({ date: to })}`, { headers: authHeaders() });
      if (!response.ok) {
        const json = await response.json().catch(() => ({}));
        throw new Error(json.error || 'Failed to export Pick & Pack report');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pick-pack-report-${to}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      setActionError(requestError.message);
    } finally {
      setDownloading(false);
    }
  };

  const rows = useMemo(() => [...(data.rows || [])].sort((left, right) => right.work_date.localeCompare(left.work_date) || right.units - left.units), [data.rows]);
  const picks = rows.filter((row) => row.operation === 'pick');
  const packs = rows.filter((row) => row.operation === 'pack');
  const trend = data.trend || [];
  const maxUnits = Math.max(0, ...trend.map((day) => Math.max(day.pickUnits, day.packUnits)));
  const noScanWeekdays = trend.filter((day) => day.status !== 'not_imported' && !day.pickLines && !day.shipments && ![0, 6].includes(new Date(`${day.date}T12:00:00Z`).getUTCDay()));
  const notImported = trend.filter((day) => day.status === 'not_imported' && day.date <= today);

  const exportButton = (
    <button type="button" onClick={downloadPdf} disabled={downloading} className="rounded-md bg-black px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-40">
      {downloading ? 'Preparing PDF…' : `Download PDF for ${mmddyyyy(to)}`}
    </button>
  );

  return (
    <ReportTemplate
      reportId={PICK_PACK_REPORT}
      title="Pick & Pack Report"
      description="Every picker and packer by day from ShipHero pick and pack scans. The same PDF posts to Slack Mon–Fri at 4:30 PM Pacific."
      breadcrumbs={[{ label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> }, { label: '' }]}
      onBack={() => navigate(ROUTES.REPORTS)}
      marginFromSidebar={marginFromSidebar}
      isLoading={loading}
      error={error}
      exportButton={exportButton}
      renderChildrenOnly
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <label className="flex items-center gap-2">
            <span className="text-gray-600">From</span>
            <input type="date" value={from} max={to} onChange={(event) => updateParams({ from: event.target.value })} className="h-8 rounded border border-gray-300 px-2 text-sm" />
          </label>
          <label className="flex items-center gap-2">
            <span className="text-gray-600">To</span>
            <input type="date" value={to} min={from} onChange={(event) => updateParams({ to: event.target.value })} className="h-8 rounded border border-gray-300 px-2 text-sm" />
          </label>
          <button type="button" onClick={() => updateParams({ from: to, to })} className="rounded-full bg-gray-100 px-3 py-1.5 text-xs text-gray-700">Single day</button>
          <button type="button" onClick={() => updateParams({ from: shiftDay(today, -6), to: today })} className="rounded-full bg-gray-100 px-3 py-1.5 text-xs text-gray-700">Last 7 days</button>
          <button type="button" onClick={() => updateParams({ from: shiftDay(today, -29), to: today })} className="rounded-full bg-gray-100 px-3 py-1.5 text-xs text-gray-700">Last 30 days</button>
          <button type="button" onClick={() => updateParams({ from: `${today.slice(0, 4)}-01-01`, to: today })} className="rounded-full bg-gray-100 px-3 py-1.5 text-xs text-gray-700">Year to date</button>
          <span className="text-gray-500">History in Eadin: {mmddyyyy(data.coverage?.earliest)} – {mmddyyyy(data.coverage?.latest)}</span>
          {actionError && <span className="text-red-600">{actionError}</span>}
        </div>

        {(noScanWeekdays.length > 0 || notImported.length > 0) && (
          <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            {noScanWeekdays.length > 0 && <div>Weekdays with no ShipHero pick or pack scans: {noScanWeekdays.map((day) => mmddyyyy(day.date)).join(', ')}</div>}
            {notImported.length > 0 && <div>Days not imported from ShipHero yet: {notImported.map((day) => mmddyyyy(day.date)).join(', ')}</div>}
          </div>
        )}

        <div>
          <h2 className="mb-2 text-sm font-semibold">Daily trend</h2>
          <div className="overflow-auto rounded border bg-white">
            <table className="min-w-max text-left text-xs">
              <thead className="sticky top-0 bg-gray-100">
                <tr>{['Date', 'Data', 'Pickers', 'Orders picked', 'Pick lines', 'Units picked', '', 'Packers', 'Shipments packed', 'Units packed', ''].map((label, index) => <th key={`${label}-${index}`} className="whitespace-nowrap px-3 py-2">{label}</th>)}</tr>
              </thead>
              <tbody>
                {[...trend].reverse().map((day, index) => (
                  <tr key={day.date} className={index % 2 ? 'bg-gray-50' : ''}>
                    <td className="whitespace-nowrap px-3 py-2">{weekday(day.date)} {mmddyyyy(day.date)}</td>
                    <td className="whitespace-nowrap px-3 py-2"><span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${STATUS_STYLE[day.status] || STATUS_STYLE.not_imported}`}>{statusLabel(day)}</span></td>
                    <td className="px-3 py-2">{formatNumber(day.pickers)}</td>
                    <td className="px-3 py-2">{formatNumber(day.pickOrders)}</td>
                    <td className="px-3 py-2">{formatNumber(day.pickLines)}</td>
                    <td className="px-3 py-2">{formatNumber(day.pickUnits)}</td>
                    <td className="px-3 py-2"><Bar value={day.pickUnits} max={maxUnits} color="bg-blue-500" /></td>
                    <td className="px-3 py-2">{formatNumber(day.packers)}</td>
                    <td className="px-3 py-2">{formatNumber(day.shipments)}</td>
                    <td className="px-3 py-2">{formatNumber(day.packUnits)}</td>
                    <td className="px-3 py-2"><Bar value={day.packUnits} max={maxUnits} color="bg-emerald-500" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          {[['pick', 'PICK', 'Picker totals', 'bg-blue-100 text-blue-800'], ['pack', 'PACK', 'Packer totals', 'bg-emerald-100 text-emerald-800']].map(([operation, tag, label, style]) => (
            <div key={operation} className="rounded border bg-white p-3 text-xs">
              <div className="mb-2 font-semibold"><span className={`mr-2 rounded px-1.5 py-0.5 text-[10px] ${style}`}>{tag}</span>{label} for the range</div>
              <table className="w-full text-left">
                <thead><tr className="text-gray-500"><th className="py-1">Person</th><th>Days</th><th>Units</th><th>{operation === 'pick' ? 'Orders' : 'Shipments'}</th><th>Units / day</th></tr></thead>
                <tbody>
                  {personTotals(rows, operation).map((entry) => (
                    <tr key={entry.name}><td className="py-1 font-semibold">{entry.name}</td><td>{entry.days}</td><td>{formatNumber(entry.units)}</td><td>{formatNumber(operation === 'pick' ? entry.orders : entry.shipments)}</td><td>{formatNumber(Math.round(entry.units / entry.days))}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>

        <div>
          <h2 className="mb-2 text-sm font-semibold"><span className="mr-2 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] text-blue-800">PICK</span>Pickers by day</h2>
          <DataTable columns={PICK_COLUMNS} rows={picks.map(pickCells)} empty="No pick scans in this range." />
        </div>
        <div>
          <h2 className="mb-2 text-sm font-semibold"><span className="mr-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] text-emerald-800">PACK</span>Packers by day</h2>
          <DataTable columns={PACK_COLUMNS} rows={packs.map(packCells)} empty="No pack scans in this range." />
        </div>

        <p className="text-xs text-gray-500">
          {DERIVED} Calculated by Eadin from ShipHero scan timestamps (first to last scan; breaks are not known). Per-hour rates show only when the span is at least 15 minutes. Gaps are observations, not alerts.
          {' '}Hero Board score, productivity, active time and alert time are not included: ShipHero answers &quot;Data is temporarily unavailable&quot; for those.
        </p>
      </div>
    </ReportTemplate>
  );
};

export default PickPackReport;
